/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {Author, BaseComponent, CompoApiService, Customer, FileService, Picture, StoreService, Task, TaskComment} from 'compob2b-libs';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {ConfirmDialogComponent} from 'compob2b-libs';
import {translations} from '../../../../../../environments/translations.ru';
import {NbDialogService} from '@nebular/theme';

@Component({
    selector: 'app-comment-issue',
    templateUrl: './comment-issue.component.html',
    styleUrls: ['./comment-issue.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CommentIssueComponent extends BaseComponent implements OnInit {
  @Input() issue: Task;
  customer: Customer;
  author: Author;
  comments: TaskComment[];
  commentNewForm: FormGroup;
  commentForm: FormGroup;
  editComment: TaskComment;
  showCommentForm = false;
  loading = false;
  direct: boolean = localStorage.getItem('commentsDirect') !== '0';

  constructor(private readonly storeService: StoreService,
              private readonly fileService: FileService,
              private readonly apiService: CompoApiService,
              private readonly fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.customer = this.storeService.getCustomer();
    this.author = {
      name: this.customer.firstName + ' ' + this.customer.lastName,
      uid: '',
      avatar: this.customer.picture
    };

    this.commentNewForm = this.fb.group({
      content: ['', Validators.required]
    });

    this.commentForm = this.fb.group({
      id: [''],
      content: ['', Validators.required]
    });

    this.loading = true;
    this.getComments();
  }

  get commentNewControl(): FormControl {
    return this.commentNewForm.controls.content as FormControl;
  }

  get commentControl(): FormControl {
    return this.commentForm.controls.content as FormControl;
  }

  getImageUri(picture: Picture): string {
    return this.fileService.getImageUri(picture, 96);
  }

  delete(comment: TaskComment) {
    this.dialogService.open(ConfirmDialogComponent, {
      context: {
        description: translations.confirmDelete + ' ' + translations.comment + '?'
      },
    }).onClose.pipe(
      filter(result => result === true),
      switchMap(_ => this.deleteComment(comment.id)),
      tap(_ => comment.deleted = true),
      tap(_ => this.cdr.detectChanges()),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  edit(comment: TaskComment) {
    this.editComment = comment;
    this.showCommentForm = false;
    this.commentForm.get('id').patchValue(comment.id);
    this.commentForm.get('content').patchValue(comment.content);
  }

  cancelComment(control: FormControl) {
    this.editComment = null;
    this.showCommentForm = false;
    control.reset();
    this.cdr.detectChanges();
  }

  saveComment(control: FormControl, comment: TaskComment) {

    if (comment === null) {
      comment = {
        crmIssueId: this.issue.id,
        content: control.value
      };
      this.storeComment(comment).pipe(
        tap(_ => this.handleStoreCommentResponse(_)),
        tap(_ => this.cancelComment(control)),
        takeUntil(this.unsubscribe)
      ).subscribe();
    } else {
      comment.content = control.value;
      this.updateComment(comment).pipe(
        tap(_ => this.cancelComment(control)),
        takeUntil(this.unsubscribe)
      ).subscribe();
    }
  }

  private deleteComment(id: number): Observable<any> {
    return this.apiService.remove('/issue/comment/' + id);
  }

  private getComments() {
    this.apiService.get<TaskComment[]>('/issue/' + this.issue.id + '/comments/' + this.direct)
      .pipe(
        map(response => response.data),
        tap((comments: TaskComment[]) => {
          this.comments = comments;
          this.loading = false;
          this.cdr.detectChanges();
        }),
        take(1),
      ).subscribe();
  }

  private updateComment(comment: TaskComment): Observable<TaskComment> {
    return this.apiService.patch<TaskComment, TaskComment>('/issue/comment/' + comment.id, comment).pipe(map(response => response.data));
  }

  private storeComment(comment: TaskComment): Observable<TaskComment> {
    return this.apiService.post<TaskComment, TaskComment>('/issue/comment', comment).pipe(map(response => response.data));
  }

  private handleStoreCommentResponse(response: TaskComment) {
    this.commentNewForm.reset();
    if (this.direct) {
      this.comments.push(response);
    } else {
      this.comments.unshift(response);
    }
    this.cdr.detectChanges();
  }

  toggleShowCommentForm() {
    this.showCommentForm = !this.showCommentForm;
    this.editComment = null;
    this.commentControl.reset();
  }

  toggleSort() {
    this.direct = !this.direct;
    localStorage.setItem('commentsDirect', this.direct ? '1' : '0');
    this.getComments();
  }
}
